import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useMessage } from '../context/MessageContext';
import { FaFacebookF, FaInstagram, FaTwitter, FaBars, FaYoutube, FaHome, FaEnvelope, FaCog, FaUser, FaSignOutAlt, FaKey, FaStar, FaSearch, FaNewspaper, FaInbox, FaQuestionCircle } from 'react-icons/fa';
import t12Logo from '../assets/t12.png';

// ... imports unverändert ...

const Navigation = () => {
  const [theme, setTheme] = useState('light');
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { unreadCount } = useMessage();

  useEffect(() => {
    const storedTheme = localStorage.getItem('theme') || 'dark';
    setTheme(storedTheme);
    document.documentElement.setAttribute('data-theme', storedTheme);
    if (storedTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="bg-white/10 backdrop-blur-lg border-b border-white/20 text-white shadow-glass sticky top-0 z-50 transition-all duration-300">
      <div className="max-w-screen-xl mx-auto px-4 flex items-center justify-between h-20">

        {/* Logo + Links */}
        <div className="flex items-center gap-6">
          <Link to="/forum" className="flex items-center gap-3 group">
            <div className="relative">
              <img src={t12Logo} alt="t12" className="h-14 w-14 object-contain transition-transform duration-300 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            <span className="font-bold text-white text-xl hidden md:inline bg-gradient-to-r from-white to-gray-200 bg-clip-text text-transparent">
              tivoli12 Forum
            </span>
          </Link>

          {/* Desktop-Menü */}
          <nav className="hidden md:flex gap-6 text-sm ml-4 text-white">
            <a 
              href="https://tivoli12.at" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="font-semibold text-white hover:text-gray-300 transition-colors duration-200"
            >
              Magazin
            </a>
          </nav>
        </div>

        {/* Rechts: Desktop-Bereich */}
        <div className="hidden md:flex items-center gap-4">
          {user && (
            <Link 
              to="/unread" 
              className="text-sm text-white hover:text-primary-200 hover:bg-white/10 px-3 py-2 rounded-lg transition-all duration-200 whitespace-nowrap font-medium"
            >
              Ungelesene Beiträge
            </Link>
          )}
          <Link 
            to="/threads/recent" 
            className="text-sm text-white hover:text-primary-200 hover:bg-white/10 px-3 py-2 rounded-lg transition-all duration-200 whitespace-nowrap font-medium"
          >
            Neueste Beiträge
          </Link>
          {user && (
            <Link 
              to="/search" 
              className="text-sm text-white hover:text-primary-200 hover:bg-white/10 px-3 py-2 rounded-lg transition-all duration-200 whitespace-nowrap font-medium"
            >
              Suche
            </Link>
          )}
          <Link 
            to="/faq" 
            className="text-sm text-white hover:text-primary-200 hover:bg-white/10 px-3 py-2 rounded-lg transition-all duration-200 whitespace-nowrap font-medium"
          >
            FAQ
          </Link>

          {!user ? (
            <>
              <Link to="/login" className="btn btn-sm glass-btn text-white border-white/30 hover:border-primary/50">
                Login
              </Link>
              <Link to="/register" className="btn btn-sm bg-primary/90 hover:bg-primary text-white border-none">
                Registrieren
              </Link>
            </>
          ) : (
            <div className="dropdown dropdown-end">
              <label tabIndex={0} className="flex items-center gap-3 text-white cursor-pointer hover:text-gray-300 transition-colors duration-200">
                {user.avatarUrl ? (
                  <img
                    src={`https://forumapi.more-features.com${user.avatarUrl}`}
                    alt="Avatar"
                    className="w-8 h-8 rounded-full object-cover ring-2 ring-white/30"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary/80 flex items-center justify-center text-sm text-white font-semibold ring-2 ring-white/30">
                    {user.username?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                )}
                <span className="flex items-center gap-2 font-medium">
                  {user.username}
                  {unreadCount > 0 && (
                    <span className="badge badge-error text-xs animate-bounce bg-red-500 text-white border-none">
                      {unreadCount}
                    </span>
                  )}
                </span>
              </label>
              <ul tabIndex={0} className="dropdown-content mt-3 z-50 menu p-3 shadow-glass bg-gray-900/95 backdrop-blur-lg text-white rounded-2xl w-56 border border-white/10">
                <li>
                  <Link to="/usercp" className="font-semibold text-white hover:bg-white/10 rounded-lg transition-colors duration-200 flex items-center gap-3">
                    <FaHome className="w-4 h-4" />
                    Benutzerbereich
                  </Link>
                </li>
                <li>
                  <Link to="/pm" className="font-semibold text-white hover:bg-white/10 rounded-lg transition-colors duration-200 flex items-center gap-3">
                    <FaEnvelope className="w-4 h-4" />
                    Private Nachrichten
                  </Link>
                </li>
                {user.isAdmin && (
                  <li>
                    <Link to="/admin" className="font-semibold text-white hover:bg-white/10 rounded-lg transition-colors duration-200 flex items-center gap-3">
                      <FaCog className="w-4 h-4" />
                      Adminbereich
                    </Link>
                  </li>
                )}
                <div className="divider my-2 border-white/20"></div>
                <li>
                  <button 
                    onClick={handleLogout} 
                    className="text-white font-bold hover:bg-red-500/20 hover:text-red-300 rounded-lg transition-colors duration-200 flex items-center gap-3"
                  >
                    <FaSignOutAlt className="w-4 h-4" />
                    Logout
                  </button>
                </li>
              </ul>
            </div>
          )}

          {/* Social Icons with improved styling */}
          <div className="flex gap-3 text-white ml-4">
            <a 
              href="https://www.facebook.com/tivoli12" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-white hover:text-gray-300 transition-all duration-200 p-2 rounded-lg hover:bg-white/10"
            >
              <FaFacebookF className="w-4 h-4" />
            </a>
            <a 
              href="https://www.instagram.com/t12.at/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-white hover:text-gray-300 transition-all duration-200 p-2 rounded-lg hover:bg-white/10"
            >
              <FaInstagram className="w-4 h-4" />
            </a>
            <a 
              href="https://www.youtube.com/@tivoli12tv" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-white hover:text-gray-300 transition-all duration-200 p-2 rounded-lg hover:bg-white/10"
            >
              <FaYoutube className="w-4 h-4" />
            </a>
            <a
              href="https://bsky.app/profile/tivoli12.bsky.social"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:text-gray-300 transition-all duration-200 p-2 rounded-lg hover:bg-white/10"
              title="Bluesky"
            >
              <svg
                role="img"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 fill-current"
              >
                <title>Bluesky</title>
                <path d="M12 10.8c-1.087 -2.114 -4.046 -6.053 -6.798 -7.995C2.566 0.944 1.561 1.266 0.902 1.565 0.139 1.908 0 3.08 0 3.768c0 0.69 0.378 5.65 0.624 6.479 0.815 2.736 3.713 3.66 6.383 3.364 0.136 -0.02 0.275 -0.039 0.415 -0.056 -0.138 0.022 -0.276 0.04 -0.415 0.056 -3.912 0.58 -7.387 2.005 -2.83 7.078 5.013 5.19 6.87 -1.113 7.823 -4.308 0.953 3.195 2.05 9.271 7.733 4.308 4.267 -4.308 1.172 -6.498 -2.74 -7.078a8.741 8.741 0 0 1 -0.415 -0.056c0.14 0.017 0.279 0.036 0.415 0.056 2.67 0.297 5.568 -0.628 6.383 -3.364 0.246 -0.828 0.624 -5.79 0.624 -6.478 0 -0.69 -0.139 -1.861 -0.902 -2.206 -0.659 -0.298 -1.664 -0.62 -4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z" fill="currentColor" />
              </svg>
            </a>
          </div>
        </div>

        {/* Mobile Menü Toggle */}
        <button 
          onClick={() => setMenuOpen(!menuOpen)} 
          className="md:hidden btn btn-sm glass-btn text-white border-white/30 hover:border-primary/50"
        >
          <FaBars className="w-4 h-4" />
        </button>
      </div>

      {/* Mobile-Menü */}
      {menuOpen && (
        <div className="md:hidden px-6 pb-6 bg-white/10 backdrop-blur-lg border-t border-white/20 text-white">
          <nav className="flex flex-col gap-3 text-sm pt-4">
            <Link 
              to="/forum" 
              onClick={() => setMenuOpen(false)} 
              className="text-white hover:text-gray-300 py-2 px-3 rounded-lg hover:bg-white/10 transition-all duration-200 font-medium flex items-center gap-3"
            >
              <FaHome className="w-4 h-4" />
              Start
            </Link>
            <a 
              href="https://tivoli12.at" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-white hover:text-gray-300 py-2 px-3 rounded-lg hover:bg-white/10 transition-all duration-200 font-medium flex items-center gap-3"
            >
              <FaNewspaper className="w-4 h-4" />
              Magazin
            </a>
            <Link 
              to="/threads/recent" 
              onClick={() => setMenuOpen(false)} 
              className="text-white hover:text-gray-300 py-2 px-3 rounded-lg hover:bg-white/10 transition-all duration-200 font-medium flex items-center gap-3"
            >
              <FaStar className="w-4 h-4" />
              Neueste Beiträge
            </Link>
            {user && (
              <Link 
                to="/unread" 
                onClick={() => setMenuOpen(false)} 
                className="text-white hover:text-gray-300 py-2 px-3 rounded-lg hover:bg-white/10 transition-all duration-200 font-medium flex items-center gap-3"
              >
                <FaInbox className="w-4 h-4" />
                Ungelesene Beiträge
              </Link>
            )}
            {user && (
              <Link 
                to="/search" 
                onClick={() => setMenuOpen(false)} 
                className="text-white hover:text-gray-300 py-2 px-3 rounded-lg hover:bg-white/10 transition-all duration-200 font-medium flex items-center gap-3"
              >
                <FaSearch className="w-4 h-4" />
                Suche
              </Link>
            )}
            <Link 
              to="/faq" 
              onClick={() => setMenuOpen(false)} 
              className="text-white hover:text-gray-300 py-2 px-3 rounded-lg hover:bg-white/10 transition-all duration-200 font-medium flex items-center gap-3"
            >
              <FaQuestionCircle className="w-4 h-4" />
              FAQ
            </Link>

            {!user ? (
              <>
                <Link 
                  to="/login" 
                  onClick={() => setMenuOpen(false)} 
                  className="text-white hover:text-gray-300 py-2 px-3 rounded-lg hover:bg-white/10 transition-all duration-200 font-medium flex items-center gap-3"
                >
                  <FaKey className="w-4 h-4" />
                  Login
                </Link>
                <Link 
                  to="/register" 
                  onClick={() => setMenuOpen(false)} 
                  className="text-white hover:text-gray-300 py-2 px-3 rounded-lg hover:bg-white/10 transition-all duration-200 font-medium flex items-center gap-3"
                >
                  <FaStar className="w-4 h-4" />
                  Registrieren
                </Link>
              </>
            ) : (
              <>
                <Link 
                  to="/usercp" 
                  onClick={() => setMenuOpen(false)} 
                  className="text-white hover:text-gray-300 py-2 px-3 rounded-lg hover:bg-white/10 transition-all duration-200 font-medium flex items-center gap-3"
                >
                  <FaUser className="w-4 h-4" />
                  Benutzerbereich
                </Link>
                <Link 
                  to="/pm" 
                  onClick={() => setMenuOpen(false)} 
                  className="text-white hover:text-gray-300 py-2 px-3 rounded-lg hover:bg-white/10 transition-all duration-200 font-medium flex items-center gap-3"
                >
                  <FaEnvelope className="w-4 h-4" />
                  Private Nachrichten
                </Link>
                {user.isAdmin && (
                  <Link 
                    to="/admin" 
                    onClick={() => setMenuOpen(false)} 
                    className="text-white hover:text-gray-300 py-2 px-3 rounded-lg hover:bg-white/10 transition-all duration-200 font-medium flex items-center gap-3"
                  >
                    <FaCog className="w-4 h-4" />
                    Adminbereich
                  </Link>
                )}
                <button 
                  onClick={() => { handleLogout(); setMenuOpen(false); }} 
                  className="text-left text-white hover:text-red-300 py-2 px-3 rounded-lg hover:bg-red-500/20 transition-all duration-200 font-medium flex items-center gap-3"
                >
                  <FaSignOutAlt className="w-4 h-4" />
                  Logout
                </button>
              </>
            )}

            {/* Social Icons */}
            <div className="flex gap-4 pt-3 justify-center border-t border-white/20 mt-3">
              <a 
                href="https://www.facebook.com/tivoli12" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-white hover:text-gray-300 p-2 rounded-lg hover:bg-white/10 transition-all duration-200"
              >
                <FaFacebookF className="w-5 h-5" />
              </a>
              <a 
                href="https://www.instagram.com/t12.at/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-white hover:text-gray-300 p-2 rounded-lg hover:bg-white/10 transition-all duration-200"
              >
                <FaInstagram className="w-5 h-5" />
              </a>
              <a 
                href="https://www.youtube.com/@tivoli12tv" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-white hover:text-gray-300 p-2 rounded-lg hover:bg-white/10 transition-all duration-200"
              >
                <FaYoutube className="w-5 h-5" />
              </a>
              <a
                href="https://bsky.app/profile/tivoli12.bsky.social"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-gray-300 p-2 rounded-lg hover:bg-white/10 transition-all duration-200"
                title="Bluesky"
              >
                <svg
                  role="img"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5 fill-current"
                >
                  <title>Bluesky</title>
                  <path d="M12 10.8c-1.087 -2.114 -4.046 -6.053 -6.798 -7.995C2.566 0.944 1.561 1.266 0.902 1.565 0.139 1.908 0 3.08 0 3.768c0 0.69 0.378 5.65 0.624 6.479 0.815 2.736 3.713 3.66 6.383 3.364 0.136 -0.02 0.275 -0.039 0.415 -0.056 -0.138 0.022 -0.276 0.04 -0.415 0.056 -3.912 0.58 -7.387 2.005 -2.83 7.078 5.013 5.19 6.87 -1.113 7.823 -4.308 0.953 3.195 2.05 9.271 7.733 4.308 4.267 -4.308 1.172 -6.498 -2.74 -7.078a8.741 8.741 0 0 1 -0.415 -0.056c0.14 0.017 0.279 0.036 0.415 0.056 2.67 0.297 5.568 -0.628 6.383 -3.364 0.246 -0.828 0.624 -5.79 0.624 -6.478 0 -0.69 -0.139 -1.861 -0.902 -2.206 -0.659 -0.298 -1.664 -0.62 -4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z" fill="currentColor" />
                </svg>
              </a>
            </div>
          </nav>
        </div>
      )}
    </div>
  );
};

export default Navigation;
